import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCode, FaExternalLinkAlt, FaCheckCircle, FaShieldAlt } from 'react-icons/fa';
import { SectionHeader, Card, Badge, Button, AnimatedSection, StaggerContainer, StaggerItem } from '../components/common';
import { personalInfo, projects } from '../data/profileData';

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);

  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'Enterprise', name: 'Enterprise' },
    { id: 'E-Learning', name: 'E-Learning' },
    { id: 'Security', name: 'Security' },
    { id: 'Cloud', name: 'Cloud' },
    { id: 'Analytics', name: 'Analytics' },
  ];

  const filteredProjects = activeFilter === 'all'
    ? projects
    : projects.filter(project => project.category === activeFilter);

  const featuredProjects = projects.filter(p => p.featured);

  return (
    <>
      <Helmet>
        <title>Projects | Rakesh Kumar Tripathi</title>
        <meta name="description" content={`Featured projects by ${personalInfo.name}. ERPNext implementation, Moodle LMS, Cybersecurity frameworks, Cloud migration, and more.`} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-8 pb-8 gradient-bg-subtle">
        <div className="container-custom">
          <AnimatedSection>
            <SectionHeader
              badge="Portfolio"
              title="Featured Projects"
              subtitle="A showcase of significant projects delivered across enterprise solutions, e-learning, security, and cloud infrastructure."
            />
          </AnimatedSection>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-2xl font-bold text-dark-800 mb-8">Highlighted Work</h2>

          <div className="grid lg:grid-cols-3 gap-6 mb-12">
            {featuredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group cursor-pointer"
                onClick={() => setSelectedProject(project)}
              >
                <Card elevated className="h-full overflow-hidden" padding="none">
                  {/* Project Image/Placeholder */}
                  <div className="h-48 bg-gradient-to-br from-primary-100 to-secondary-100 flex items-center justify-center relative overflow-hidden">
                    <FaCode className="w-16 h-16 text-primary-300" />
                    <div className="absolute inset-0 bg-gradient-to-t from-dark-800/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                      <span className="text-white text-sm font-medium">View Details</span>
                    </div>
                    <Badge
                      variant="secondary"
                      className="absolute top-4 right-4"
                    >
                      Featured
                    </Badge>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <Badge variant="outline" size="sm" className="mb-3">
                      {project.category}
                    </Badge>
                    <h3 className="text-xl font-bold text-dark-800 mb-2 group-hover:text-primary-500 transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-dark-500 text-sm mb-4 line-clamp-2">
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.slice(0, 3).map((tech) => (
                        <Badge key={tech} variant="primary" size="sm">
                          {tech}
                        </Badge>
                      ))}
                      {project.technologies.length > 3 && (
                        <Badge variant="dark" size="sm">
                          +{project.technologies.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* All Projects Section */}
      <section className="section-padding gradient-bg-subtle">
        <div className="container-custom">
          <SectionHeader
            badge="All Work"
            title="Project Portfolio"
            subtitle="Browse all projects by category"
          />

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                onClick={() => setActiveFilter(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeFilter === category.id
                    ? 'bg-primary-500 text-white shadow-lg'
                    : 'bg-white text-dark-600 hover:bg-gray-100 shadow-soft'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {category.name}
              </motion.button>
            ))}
          </div>

          {/* Projects Grid */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeFilter}
              className="grid md:grid-cols-2 gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => setSelectedProject(project)}
                  className="cursor-pointer"
                >
                  <Card elevated className="h-full">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <Badge variant="outline" size="sm" className="mb-2">
                          {project.category}
                        </Badge>
                        <h3 className="text-lg font-bold text-dark-800 hover:text-primary-500 transition-colors">
                          {project.title}
                        </h3>
                      </div>
                      <FaExternalLinkAlt className="w-4 h-4 text-dark-400" />
                    </div>

                    <p className="text-dark-500 text-sm mb-4">
                      {project.description}
                    </p>

                    {/* Outcomes Preview */}
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-2">
                        {project.outcomes.slice(0, 2).map((outcome, i) => (
                          <span
                            key={i}
                            className="text-xs text-secondary-600 bg-secondary-50 px-2 py-1 rounded"
                          >
                            {outcome}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Technologies */}
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech) => (
                        <Badge key={tech} variant="primary" size="sm">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-800/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-start justify-between">
                  <div>
                    <Badge variant="primary" className="mb-2">
                      {selectedProject.category}
                    </Badge>
                    <h2 className="text-2xl font-bold text-dark-800">
                      {selectedProject.title}
                    </h2>
                  </div>
                  <button
                    onClick={() => setSelectedProject(null)}
                    className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                  >
                    <span className="text-dark-500">&times;</span>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-dark-600 mb-6">{selectedProject.description}</p>

                {/* Challenges */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-dark-700 uppercase tracking-wider mb-3">
                    Challenges Addressed
                  </h3>
                  <ul className="space-y-2">
                    {selectedProject.challenges.map((challenge, i) => (
                      <li key={i} className="flex items-start space-x-2">
                        <span className="w-5 h-5 rounded bg-accent-100 text-accent-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {i + 1}
                        </span>
                        <span className="text-dark-600 text-sm">{challenge}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Outcomes */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-dark-700 uppercase tracking-wider mb-3">
                    Key Outcomes
                  </h3>
                  <ul className="space-y-2">
                    {selectedProject.outcomes.map((outcome, i) => (
                      <li key={i} className="flex items-start space-x-2">
                        <FaCheckCircle className="w-4 h-4 text-secondary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-dark-600 text-sm">{outcome}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Technologies */}
                <div>
                  <h3 className="text-sm font-semibold text-dark-700 uppercase tracking-wider mb-3">
                    Technologies Used
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.technologies.map((tech) => (
                      <Badge key={tech} variant="outline">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-100">
                <Button
                  variant="primary"
                  onClick={() => setSelectedProject(null)}
                  fullWidth
                >
                  Close
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cyber Security Portfolio Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <SectionHeader
            badge="Cyber Security"
            title="Cyber Security Portfolio"
            subtitle="Course projects completed during PG Program in Cyber Security from Great Lakes"
          />

          {/* Portfolio Link */}
          <div className="mb-8 text-center">
            <a
              href="https://eportfolio.mygreatlearning.com/rakesh-kumar-tripathi2"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-primary-500 hover:text-primary-600 font-medium text-lg"
            >
              <FaExternalLinkAlt className="w-4 h-4" />
              <span>View Full Portfolio on Great Learning</span>
            </a>
          </div>

          {/* Course Projects */}
          <div className="space-y-6">
            {[
              {
                date: 'May 2023',
                title: 'Port Scan using Nmap',
                course: 'Foundations of Information Security',
                description: 'In this project candidate used Nmap for network discovery scan, nmap scan, service detection scan etc. Also checked windows firewall profiles (domain, private, public), saw the impact of firewall rules on port scanning and analysed firewall log.',
                skills: ['Kali Linux', 'NMap', 'Windows Firewall', 'Log Analysis'],
              },
              {
                date: 'Mar 2023',
                title: 'Incident Reporting and Documentation',
                course: 'Security Operations & Incident Management',
                description: 'Cybersecurity incident reporting is a tool in an organization\'s armory and part of a layered defense system. This provides the framework for effective incident management. In this project, we researched the NotPetya ransomware attack, focusing specifically on the chain of events at Maersk. We established the sequence of events and timelines of the incident and assessed the total number of assets infected. We designed an Incident Report with an executive summary for the senior leadership.',
                skills: ['Incident Management', 'Incident Response', 'Incident Reports', 'Executive Summary'],
              },
              {
                date: 'Mar 2023',
                title: 'Windows Firewall Logs',
                course: 'Security Operations & Incident Management',
                description: 'Windows Firewall is a default feature of Microsoft Windows & it can enhance the security of an entire network. Whenever a firewall accepts or drops traffic, it logs the traffic using log lines. Analyzing the logs helps in developing insights on the network activity. In this project we analyzed a Windows Firewall log file to detect and identify malicious activity. We identified the port scanning attempts, malicious single port connection requests & identified bad outgoing packets.',
                skills: ['Windows Firewall Logs', 'Investigation', 'Firewall Logging', 'TCP Flags', 'Firewall Events', 'Log Analysis'],
              },
              {
                date: 'Mar 2023',
                title: 'Windows Investigations',
                course: 'Security Operations & Incident Management',
                description: 'As a part of the SOC team, security professionals have to perform investigations on compromised assets upon a breach. As a part of the investigation, we analyzed the event logs and used the command prompt to identify the chain of events. This investigation further ascertained the timelines of the compromise and the compromised User account. Upon completion of the project, we provided a summary report of the malicious activities and recommended the next phase of the investigation.',
                skills: ['Command Prompt', 'Windows Event Viewer', 'SSH Server', 'Event ID', 'Process ID', 'Event Logs', 'Virus Total'],
              },
              {
                date: 'Feb 2023',
                title: 'Vulnerability Management',
                course: 'Designing Security Controls',
                description: 'Cyber vulnerabilities include weaknesses or issues in the hardware, software, or systems of IT infrastructure within an organization. In this project, we analyzed the vulnerability scan results and derived the current status of the Vulnerability management program of an organization. We identified the systems with the highest number of vulnerabilities, the most critical vulnerabilities present across the organization, and the vulnerabilities with the highest age.',
                skills: ['Vulnerability Scanning Reports', 'Vulnerability Scanning'],
              },
              {
                date: 'Feb 2023',
                title: 'Vulnerability Management with Nessus',
                course: 'Designing Security Controls',
                description: 'It is commonly seen that adversaries take advantage of the existing vulnerabilities in a network for conducting an attack. Nessus is one of the most widely-used vulnerability scanners worldwide. In this project, we performed vulnerability scans on a computer system using Nessus. We identified vulnerabilities rated as Critical, High, Medium and Low. By Analyzing the detected vulnerabilities, we proposed recommendations to curb these vulnerabilities.',
                skills: ['Vulnerability Management', 'Vulnerability Management Reports', 'Nessus'],
              },
              {
                date: 'Jan 2023',
                title: 'Endpoint Detection and Response (EDR)',
                course: 'Designing Security Controls',
                description: 'Endpoint Detection and Response enables continuous monitoring and analysis of the endpoints across a network. Cyber security teams all over the world have to do product evaluations on a regular basis to select a product as per the requirements. In this project, we evaluated three EDR products and mapped them to the business requirements of a particular organization. On the basis of the suitability of the product, we enumerated the best-recommended product for the organization.',
                skills: ['EDR', 'EDR Solutions', 'Product Evaluations', 'Risk Analysis', 'Risk Control Mapping', 'Security Analysis'],
              },
              {
                date: 'Jan 2023',
                title: 'Ransomware Attacks',
                course: 'Understanding Cyber Attacks',
                description: 'Ransomware is a type of malware attack in which the attacker locks and encrypts the victim\'s data, and important files and then demands a payment to unlock and decrypt the data. In this project, we performed threat research on a ransomware threat group. We identified the origins, motivation, past successful attacks, and IoCs (Indicators of Compromise) and successfully enumerated the preventive measures for protection from this threat group.',
                skills: ['Ransomware', 'Threat Reports', 'IoC'],
              },
              {
                date: 'Jan 2023',
                title: 'Cracking a Password',
                course: 'Understanding Cyber Attacks',
                description: 'Brute-force and dictionary attacks are both cybersecurity attacks in which the attacker attempts to log into an account by using different passwords to find the correct one. These common attacks often succeed because many users use common variations on a few passwords. In this project, we performed brute-force attacks and dictionary attacks by using tools such as L0phtcrack, Ophcrack and John the Ripper.',
                skills: ['Password Cracking', 'Penetration Testing', 'L0phtcrack', 'Ophcrack', 'John the Ripper', 'Bruteforce Attacks', 'Cryptanalysis'],
              },
              {
                date: 'Dec 2022',
                title: 'Social Engineering Attack Analysis',
                course: 'Understanding Cyber Attacks',
                description: 'Phishing emails are deceptive emails that are used by an attacker to gain confidential information from the victim. It is a type of social engineering attack. In this project, we analyzed 5 suspicious-looking emails to classify if these emails were phishing attempts. Further, we analyzed the external links for legitimacy using web tools.',
                skills: ['Phishing Emails', 'Social Engineering Attacks', 'urlscan.io'],
              },
              {
                date: 'Dec 2022',
                title: 'MITRE ATT&CK Framework',
                course: 'Understanding Cyber Attacks',
                description: 'The MITRE ATT&CK framework is a repository of knowledge for the behavior of cyber adversaries. It provides the details of various phases of the attack lifecycle and the targeted platforms. The tactics and techniques in the framework provide a categorization of individual adversaries. In this project, we analyzed two threat adversaries and structured the threat information for use in Threat Intelligence. We analyzed the associated groups, tactics, techniques and procedures of the two threat group.',
                skills: ['MITRE ATT&CK', 'Threat Intelligence', 'Attack Vectors', 'Attack Surface', 'Threat Model'],
              },
              {
                date: 'Dec 2022',
                title: 'Wireshark Packet Analysis',
                course: 'Foundations of Information Security',
                description: 'In this project, candidate learn to analyze the HTTP traffic, identify a 3-way TCP handshake from packet captures and analyze DNS traffic. Performed hands-on tasks on Wireshark with capture filters, display filters and DNS filters. Wireshark is the world\'s foremost and widely-used network protocol analyzer.',
                skills: ['Wireshark Packet Analysis', 'HTTP Traffic', '3-way TCP Handshake', 'DNS Traffic', 'Filters'],
              },
              {
                date: 'Dec 2022',
                title: 'SSL Certificate Analysis',
                course: 'Foundations of Information Security',
                description: 'In this project, the candidate has practiced the skills for understanding an analyzing an SSL certificate for specific details. SSL is a security technology for establishing an encrypted link between a server and a client, such as a website and a browser, or a pair of email servers. An SSL certificate is a digital certificate that authenticates a website\'s identity and enables an encrypted connection.',
                skills: ['Digital Certificates', 'SSL', 'Certificate Revocation List (CRL)'],
              },
            ].map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Card elevated>
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                        <FaShieldAlt className="w-6 h-6 text-primary-500" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge variant="secondary" size="sm">{project.date}</Badge>
                        <Badge variant="outline" size="sm">{project.course}</Badge>
                      </div>
                      <h3 className="text-lg font-bold text-dark-800 mb-2">{project.title}</h3>
                      <p className="text-dark-500 text-sm mb-4">{project.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {project.skills.map((skill) => (
                          <Badge key={skill} variant="primary" size="sm">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-dark-800">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Have a Project in Mind?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mb-8">
              I'm available for consulting, implementation, and training services.
              Let's discuss how I can help with your next project.
            </p>
            <Button variant="primary" size="lg" href="/contact">
              Start a Conversation
            </Button>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Projects;
